import React from 'react';
import {
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { AiChatPage } from '../AiChatPage';
import { ModelSwitcher } from '../../../common/ModelSwitcher';
import { useModelSwitcher } from '../../../../hooks/useModelSwitcher';
import { selectIsSuperAdmin, selectIsUniversalAdmin } from '../../../store/selectors/session';
import { RootState } from '../../../store/reducers';

const useStyles = makeStyles((theme: any) => ({
    chatContainer: {
        backgroundColor: '#ffffff',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        position: 'relative',
        height: '100%',
    },
    chatBody: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    actionButton: {
        width: '32px',
        height: '32px',
        minWidth: '32px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        color: '#666',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#999',
        },
    },
}));

interface ChatSidebarWrapperProps {
    open: boolean;
    onClose?: () => void;
    contractId?: string | number;
    sessionId?: string | number;
    directoryId?: string;
    claimNumber?: string;
    selected?: any[];
    chatType: 'contract' | 'claim';
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    isPatientDetailsVisible?: boolean;
}

const ChatSidebarWrapper: React.FC<ChatSidebarWrapperProps> = ({
    open,
    onClose,
    contractId,
    sessionId,
    directoryId,
    claimNumber,
    selected = [],
    chatType,
    isExpanded = false,
    onExpandToggle,
    isPatientDetailsVisible = false,
}) => {
    const classes = useStyles();

    // ── Permission gate ───────────────────────────────────────────────────────
    const isSuperAdmin = useSelector<RootState>(selectIsSuperAdmin) as boolean;
    const isUniversalAdmin = useSelector<RootState>(selectIsUniversalAdmin) as boolean;

    // ── Per-session, per-chatbot model overrides ──────────────────────────────
    const {
        reasoningModel,
        quickModel,
        selectModel,
        isOverridden,
        availableModels,
        isPending: isModelsPending
    } = useModelSwitcher(chatType, isUniversalAdmin);

    // Minimize left sidebar when chat opens
    React.useEffect(() => {
        if (open) {
            if (!document.body.classList.contains('kt-aside--minimize')) {
                document.body.classList.add('kt-aside--minimize');
            }
        }
    }, [open]);

    if (!open) return null;

    // ── Header Actions (icon buttons on the far right) ────────────────────────
    const headerActions = (
        <React.Fragment>
            {isExpanded ? (
                <Tooltip title="Minimize">
                    <IconButton size="small" className={classes.actionButton} onClick={onExpandToggle}>
                        <CloseFullscreenIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Expand">
                    <IconButton size="small" className={classes.actionButton} onClick={onExpandToggle}>
                        <CropSquareIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}

            {onClose && (
                <Tooltip title="Close">
                    <IconButton size="small" className={classes.actionButton} onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
        </React.Fragment>
    );

    // ── Model Switcher Slot (Full-width row) ──────────────────────────────────
    const modelSwitcherSlot = isUniversalAdmin ? (
        <ModelSwitcher
            reasoningModel={reasoningModel}
            quickModel={quickModel}
            availableModels={availableModels}
            onModelChange={selectModel}
            isOverridden={isOverridden}
        />
    ) : null;

    return (
        <Box
            className={classes.chatContainer}
            sx={{
                width: isExpanded ? '100%' : '400px',
                minWidth: isExpanded ? '100%' : '400px',
                maxWidth: isExpanded ? '100%' : '400px',
                marginLeft: isExpanded ? 0 : '16px',
                height: '100%',
                maxHeight: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box className={classes.chatBody} sx={{
                flex: 1,
                overflow: 'auto',
                height: '100%',
            }}>
                <AiChatPage
                    selected={selected}
                    height="100%"
                    defaultTitle={chatType === 'claim' ? 'Claims SlicedIQ' : 'Contracts SlicedIQ'}
                    isEmbedded={true}
                    chatType={chatType}
                    match={{ params: {
                        contractId: contractId,
                        sessionId: sessionId,
                        directoryId: directoryId,
                    }}}
                    claimNumber={claimNumber}
                    headerActions={headerActions}
                    isSidebarMode={!isExpanded}
                    newChatbutton={{ enable: true, text: 'New Chat' }}
                    enableIconTitle={true}
                    queryContractId={contractId}
                    querySessionId={sessionId}
                    isPatientDetailsVisible={isPatientDetailsVisible}
                    reasoningModel={reasoningModel}
                    quickModel={quickModel}
                    availableModels={availableModels}
                    modelSwitcherSlot={modelSwitcherSlot}
                />
            </Box>
        </Box>
    );
};

export default ChatSidebarWrapper;

